import { type FormEvent, useState } from "react";
import { useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { CONTACT_TYPE_LABEL } from "../../lib/format";
import { useOrganizations, useUpdateContact } from "../../lib/queries";
import { type Contact, type ContactType, PROVINCES } from "../../lib/types";

const orNull = (v: FormDataEntryValue | null) => {
  const s = String(v ?? "").trim();
  return s === "" ? null : s;
};

/** Edits a contact's card. Used in the inbox side panel and on the contact page. */
export function ContactForm({ contact, compact = false }: { contact: Contact; compact?: boolean }) {
  const update = useUpdateContact(contact.id);
  const orgs = useOrganizations();
  const toast = useToast();
  const [dirty, setDirty] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      await update.mutateAsync({
        name: orNull(f.get("name")),
        type: orNull(f.get("type")) as ContactType | null,
        province: orNull(f.get("province")),
        email: orNull(f.get("email")),
        organizationId: orNull(f.get("organizationId")),
        notes: orNull(f.get("notes")),
        tags: String(f.get("tags") ?? "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      });
      setDirty(false);
      toast("Contacto guardado");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo guardar.");
    }
  }

  const id = (name: string) => `${contact.id}-${name}`;

  return (
    <form className="section" onSubmit={onSubmit} onChange={() => setDirty(true)} style={{ gap: compact ? 10 : 14 }}>
      <div className="field">
        <label htmlFor={id("name")}>Nombre</label>
        <input
          className="input"
          id={id("name")}
          name="name"
          placeholder="Ej.: María Solís"
          defaultValue={contact.name ?? ""}
          maxLength={120}
        />
      </div>
      <div className="field">
        <label htmlFor={id("type")}>Tipo de cliente</label>
        <select className="select" id={id("type")} name="type" defaultValue={contact.type ?? ""}>
          <option value="">Sin definir</option>
          {Object.entries(CONTACT_TYPE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor={id("org")}>Empresa</label>
        <select className="select" id={id("org")} name="organizationId" defaultValue={contact.organizationId ?? ""}>
          <option value="">Ninguna</option>
          {orgs.data?.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor={id("province")}>Provincia</label>
        <select className="select" id={id("province")} name="province" defaultValue={contact.province ?? ""}>
          <option value="">Sin definir</option>
          {PROVINCES.map((p) => (
            <option key={p}>{p}</option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor={id("email")}>Correo</label>
        <input
          className="input"
          id={id("email")}
          name="email"
          type="email"
          placeholder="maria@correo.com"
          defaultValue={contact.email ?? ""}
        />
      </div>
      <div className="field">
        <label htmlFor={id("tags")}>Etiquetas (separadas por coma)</label>
        <input
          className="input"
          id={id("tags")}
          name="tags"
          placeholder="Ej.: mayorista, Liberia"
          defaultValue={contact.tags.join(", ")}
        />
      </div>
      <div className="field">
        <label htmlFor={id("notes")}>Notas</label>
        <textarea
          className="textarea"
          id={id("notes")}
          name="notes"
          placeholder="Lo que el equipo debe saber de este contacto"
          defaultValue={contact.notes ?? ""}
          maxLength={5000}
        />
      </div>
      <button className="btn btn-primary" type="submit" disabled={!dirty || update.isPending}>
        {update.isPending ? "Guardando…" : "Guardar"}
      </button>
    </form>
  );
}
